import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Tabs,
  Tab,
  useTheme
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { globalVariables } from "../../global/GlobalVariables";
import {
  findGetParameter,
  getData
} from "../../components/utilities/UtilityFunctions";
import { postNotification } from "../../components/utilities/Notifications";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import {
  getEnvironmentVariable,
  isBrand,
  supportsUYPlatformFeatures
} from "../../global/environmentVariables";
import { getLanguageCookie } from "../../language/LanguageFunctions";
import Hero from "../../components/layout/Hero";
import SubscriptionCard from "./components/SubscriptionCard";
import { handlePagingAll } from "../../components/api/APIUtilities";
import { useNavigate } from "react-router-dom";
import {
  useProcessing,
  useSignout
} from "../../components/user/BCPAuthReducers";
import { signout } from "../../components/user/BCPAuth";
import OrganizationSubscriptionBlock from "./components/OrganizationSubscriptionBlock";
import { stringToUrl } from "../events/utils/event";

const SYSTEM_IMAGES_CLOUDFRONT = getEnvironmentVariable(
  "SYSTEM_IMAGES_CLOUDFRONT"
);
const SUPPORTS_UY_PLATFORM_FEATURES = supportsUYPlatformFeatures();
const IS_GW = isBrand("gw");
const IS_BCP = isBrand("bcp");
const SUPPORTS_BCP_PLATFORM_FEATURES =
  getEnvironmentVariable("SUPPORTS_BCP_PLATFORM_FEATURES") === "true";

const HAS_LARGE_BANNER_LOGO =
  getEnvironmentVariable("HAS_LARGE_BANNER_LOGO") === "true";

export default function Subscription(props) {
  const {
    // affiliateLabel,
    frequencies,
    containerSX,
    // hideLabels,
    // hideCircuitSuite,
    hidePlayerSubs
  } = props;

  const theme = useTheme();
  const language = getLanguageCookie();
  const { t } = useTranslation("translation", {
    lng: language,
    keyPrefix: "pages.subscription"
  });

  const [tabStates, setTabStates] = useState({
    player: "Monthly",
    organization: "Monthly"
  });

  const authContext = useContext(AuthContext);
  const globalVars = globalVariables();

  const affiliate = props.affiliate || findGetParameter("affiliate");
  const coupon = props.coupon || findGetParameter("coupon");
  const couponCode = props.couponCode
    ? props.couponCode
    : !coupon
      ? null
      : globalVars?.pricing?.products?.[
          globalVars?.pricing?.subscriptions?.playerSub
        ]?.coupons?.[coupon.toUpperCase()];
  // const subFeature = findGetParameter("subFeature");
  const orgId = props?.orgId ? props.orgId : findGetParameter("orgId");

  // const [gameStore, setGameStore] = useState(null);
  const [userSubscriptions, setUserSubscriptions] = useState({
    playerSubscription: {
      isSubscribed: false,
      type: null // 'Monthly' or 'Yearly'
    },
    organizationSubscription: {
      isSubscribed: false,
      type: null // 'Monthly' or 'Yearly'
    }
  });
  // const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  const navigate = useNavigate();
  const useSignoutReducer = useSignout();
  const useProcessingReducer = useProcessing();

  useEffect(() => {
    const go = async () => {
      if (!authContext?.authState?.identity?.userId) {
        // setLoading(false);
        return;
      }
      try {
        var apiUser = await getData(
          `/users/${authContext?.authState?.identity?.userId}`,
          {
            queryStringParameters: {
              "expand[]": ["team", "bankAccount"]
            }
          }
        );

        if (apiUser) {
          if (!apiUser.socials) {
            apiUser.socials = {};
          } else if (Array.isArray(apiUser.socials)) {
            var socials = {};
            apiUser.socials.forEach((social) => {
              socials[social.type] = social.username;
            });
            apiUser.socials = socials;
          }
          setUser(apiUser);
          // setLoading(false);

          if (!IS_GW) {
            if (apiUser.profileFileId || apiUser.bannerFileId) {
              if (apiUser.profileFileId) {
                apiUser.profileFile = await getData(
                  `/files/${apiUser.profileFileId}`,
                  {
                    queryStringParameters: {}
                  }
                );
              }
              if (apiUser.bannerFileId) {
                apiUser.bannerFile = await getData(
                  `/files/${apiUser.bannerFileId}`,
                  {
                    queryStringParameters: {}
                  }
                );
              }
              setUser(apiUser);
            }
          }
        }
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          try {
            if (SUPPORTS_BCP_PLATFORM_FEATURES) {
              navigate("/logout");
            } else {
              await signout(useSignoutReducer, {
                setProcessing: useProcessingReducer,
                navigate: navigate
              });
            }
          } catch (err) {
            postNotification("error", err);
          }
          navigate(`/login?redirect_url=${stringToUrl(window.location.href)}`);
          return;
        } else {
          postNotification("error", err);
        }
      }
    };

    go();

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const fetchPlayerSubscriptions = async () => {
      return await handlePagingAll(`/subscriptions`, {
        userId: user.id,
        subscriptionType: "subscription"
      });
    };

    const fetchOrganizationSubscriptions = async () => {
      return await handlePagingAll(`/subscriptions`, {
        userId: user.id,
        subscriptionType: "organization"
      });
    };

    async function parallel() {
      if (!user?.id) return;

      try {
        const [apiSubscriptions, apiOrganizationSubscriptions] =
          await Promise.all([
            fetchPlayerSubscriptions(),
            fetchOrganizationSubscriptions()
          ]);

        const playerSub = apiSubscriptions?.find(
          (sub) => sub.active && sub.collection !== "BCPStoreSubscription"
        );

        const orgSub = apiOrganizationSubscriptions?.find(
          (sub) => sub.active && sub.collection === "BCPStoreSubscription"
        );

        setUserSubscriptions({
          playerSubscription: {
            isSubscribed: !!playerSub,
            type:
              playerSub?.stripe?.plan?.interval === "year"
                ? "Yearly"
                : playerSub?.stripe?.plan?.interval === "month"
                  ? "Monthly"
                  : null
          },
          organizationSubscription: {
            isSubscribed: !!orgSub,
            type:
              orgSub?.stripe?.plan?.interval === "year"
                ? "Yearly"
                : orgSub?.stripe?.plan?.interval === "month"
                  ? "Monthly"
                  : null
          }
        });
      } catch (err) {
        console.error("Error fetching subscriptions:", err);
        postNotification("error", err);
      }
    }

    parallel();
  }, [user]);

  return (
    <Box sx={{ width: "100%" }}>
      <Hero
        backgroundImage={`${SYSTEM_IMAGES_CLOUDFRONT}/images/header.jpg`}
        fallbackImage={`${SYSTEM_IMAGES_CLOUDFRONT}/images/home1.jpg`}
        sxBackground={{
          height: ["50vh", "55vh"],
          maxHeight: ["450px", "550px"],
          minHeight: ["350px", "350px"],
          color: "common.white"
        }}
      >
        <Box
          sx={{
            maxWidth: "lg",
            p: { xs: 2, sm: 4 },
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            gap: 2
          }}
        >
          <img
            src={`${SYSTEM_IMAGES_CLOUDFRONT}/images/BannerLogo${theme?.palette?.mode === "dark" || IS_BCP ? "Dark" : ""}${HAS_LARGE_BANNER_LOGO ? "Large" : ""}.png`}
            alt="logo"
            style={{
              maxHeight: "120px",
              marginRight: "8px",
              width: "auto",
              maxWidth: "80%"
            }}
          />

          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.5rem", sm: "2rem" },
              px: { xs: 2, sm: 0 },
              lineHeight: 1.2,
              maxWidth: "800px",
              margin: "0 auto",
              color: "common.white"
            }}
          >
            {t("heroTitle")} {globalVars?.site?.title}
          </Typography>
        </Box>
      </Hero>

      <Container sx={{ pt: 2, pb: 2, ...containerSX }} maxWidth="lg">
        <Grid container sx={{ gap: 2 }}>
          <Grid container justifyContent="center" sx={{ mb: 4 }}>
            <Tabs
              value={tabStates.player}
              onChange={(event, newValue) =>
                setTabStates((prev) => ({
                  ...prev,
                  player: newValue
                }))
              }
              aria-label="Subscription tabs"
              sx={{
                "& .MuiTabs-indicator": {
                  display: "none"
                },
                "& .MuiTab-root": {
                  color: theme.palette.banner?.contrastText,
                  margin: "0 8px",
                  borderRadius: 1,
                  fontWeight: 700,
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "white"
                  }
                },
                backgroundColor: "transparent"
              }}
            >
              <Tab
                label={t("monthlyPricing")}
                value="Monthly"
                sx={{
                  minWidth: "150px",
                  borderRadius: 1,
                  fontWeight: 700
                }}
              />
              <Tab
                label={t("annualPricing")}
                value="Yearly"
                sx={{
                  minWidth: "150px",
                  borderRadius: 1,
                  fontWeight: 700
                }}
              />
            </Tabs>
          </Grid>

          {!hidePlayerSubs &&
            !orgId &&
            globalVars?.pricing?.subscriptions?.playerSub && (
              <Grid
                container
                spacing={3}
                justifyContent="center"
                sx={{ px: { xs: 2, sm: 0 }, mb: 4 }}
              >
                {tabStates.player === "Monthly" ? (
                  <>
                    <Grid item xs={12} sm={6}>
                      <SubscriptionCard
                        title={t("adventurerTitle")}
                        forWho={t("forPlayers")}
                        price={0}
                        currency="USD"
                        interval={t("perMonth")}
                        benefits={[
                          {
                            text: t("searchAndRegisterForEvents"),
                            isAvailable: true
                          },
                          {
                            text: t("findAndFollowOrganizations"),
                            isAvailable: true
                          },
                          {
                            text: t("hostPrivateCasualEvents"),
                            isAvailable: SUPPORTS_UY_PLATFORM_FEATURES ? false : true
                          },
                          { text: t("viewOrSearchLists"), isAvailable: false }
                        ]}
                        buttonText={
                          user
                            ? t("alreadySignedUp")
                            : t("signUpForFreeAccount")
                        }
                        buttonLink="/signup"
                        isDisabled={!!user}
                        sx={{ height: "100%", minHeight: "550px" }}
                      />
                    </Grid>
                    {globalVars?.pricing?.products?.[
                      globalVars?.pricing?.subscriptions?.playerSub
                    ]?.prices
                      ?.filter((price) => price.frequency === "Monthly")
                      .map((price, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                          <SubscriptionCard
                            title={t("warriorTitle")}
                            forWho={t("forPlayers")}
                            price={price.amount}
                            currency={price.currency || "USD"}
                            interval={t("perMonth")}
                            benefits={[
                              {
                                text: t("searchAndRegisterForEvents"),
                                isAvailable: true
                              },
                              {
                                text: t("findAndFollowOrganizations"),
                                isAvailable: true
                              },
                              {
                                text: t("hostPrivateCasualEvents"),
                                isAvailable: true
                              },
                              {
                                text: t("viewOrSearchLists"),
                                isAvailable: true
                              }
                            ]}
                            buttonText={
                              user &&
                              userSubscriptions.playerSubscription.isSubscribed
                                ? userSubscriptions.playerSubscription.type ===
                                  "Monthly"
                                  ? t("activePlan")
                                  : t("manageActivePlan")
                                : t("subscribe")
                            }
                            buttonLink={
                              user &&
                              userSubscriptions.playerSubscription.isSubscribed
                                ? "/account?active_tab=subscriptions"
                                : `/subscription/new?affiliate=${
                                    affiliate ||
                                    props?.affiliate ||
                                    (getEnvironmentVariable("APP_NAME")
                                      ? getEnvironmentVariable(
                                          "APP_NAME"
                                        ).toLowerCase()
                                      : "bcp")
                                  }&priceId=${price.priceId}&productId=${
                                    globalVars?.pricing?.subscriptions
                                      ?.playerSub
                                  }${
                                    couponCode?.frequencies?.[price.frequency]
                                      ? `&coupon=${couponCode.code}`
                                      : frequencies?.[price.frequency]
                                            ?.couponCode
                                        ? `&coupon=${frequencies[price.frequency].couponCode}`
                                        : ""
                                  }`
                            }
                            isDisabled={false}
                            isPromoted={true}
                            sx={{ height: "100%", minHeight: "550px" }}
                          />
                        </Grid>
                      ))}
                  </>
                ) : (
                  // Annual pricing
                  globalVars?.pricing?.products?.[
                    globalVars?.pricing?.subscriptions?.playerSub
                  ]?.prices
                    ?.filter((price) => price.frequency === "Yearly")
                    .map((price, index) => {
                      const monthlyPrice =
                        globalVars?.pricing?.products?.[
                          globalVars?.pricing?.subscriptions?.playerSub
                        ]?.prices?.find((p) => p.frequency === "Monthly")
                          ?.amount || 0;
                      const yearlyEquivalent = monthlyPrice * 12;
                      const savings =
                        yearlyEquivalent > 0
                          ? Math.round(yearlyEquivalent - price.amount)
                          : null;

                      return (
                        <Grid item xs={12} sm={6} key={index}>
                          <SubscriptionCard
                            title="Warrior"
                            forWho={t("forPlayers")}
                            price={price.amount}
                            currency={price.currency || "USD"}
                            interval={t("perYear")}
                            savingsAmount={savings}
                            benefits={[
                              {
                                text: t("searchAndRegisterForEvents"),
                                isAvailable: true
                              },
                              {
                                text: t("hostPrivateCasualEvents"),
                                isAvailable: true
                              },
                              {
                                text: t("findAndFollowOrganizations"),
                                isAvailable: true
                              },
                              {
                                text: t("viewOrSearchLists"),
                                isAvailable: true
                              }
                            ]}
                            buttonText={
                              user &&
                              userSubscriptions.playerSubscription.isSubscribed
                                ? userSubscriptions.playerSubscription.type ===
                                  "Yearly"
                                  ? t("activePlan")
                                  : t("manageActivePlan")
                                : t("subscribe")
                            }
                            buttonLink={
                              user &&
                              userSubscriptions.playerSubscription.isSubscribed
                                ? "/account?active_tab=subscriptions"
                                : `/subscription/new?affiliate=${
                                    affiliate ||
                                    props?.affiliate ||
                                    (getEnvironmentVariable("APP_NAME")
                                      ? getEnvironmentVariable(
                                          "APP_NAME"
                                        ).toLowerCase()
                                      : "bcp")
                                  }&priceId=${price.priceId}&productId=${
                                    globalVars?.pricing?.subscriptions
                                      ?.playerSub
                                  }${
                                    couponCode?.frequencies?.[price.frequency]
                                      ? `&coupon=${couponCode.code}`
                                      : frequencies?.[price.frequency]
                                            ?.couponCode
                                        ? `&coupon=${frequencies[price.frequency].couponCode}`
                                        : ""
                                  }`
                            }
                            isDisabled={false}
                            isPromoted={true}
                            sx={{ height: "100%", minHeight: "550px" }}
                          />
                        </Grid>
                      );
                    })
                )}
              </Grid>
            )}
        </Grid>
      </Container>

      {/* ORGANIZATION SUBSCRIPTIONS */}

      <OrganizationSubscriptionBlock
        tabStates={tabStates}
        setTabStates={setTabStates}
        user={user}
        userSubscriptions={userSubscriptions}
        globalVars={globalVars}
        affiliate={affiliate}
        props={props}
        SYSTEM_IMAGES_CLOUDFRONT={SYSTEM_IMAGES_CLOUDFRONT}
        orgId={orgId}
      />
    </Box>
  );
}
